/**
 * Shape canonici dei select PostgREST riusabili.
 *
 * Motivazione: F31 bug — l'embed dei materiali sotto estimate_stratigraphies
 * non includeva supplier_id, e di conseguenza l'export "Ordine fornitore"
 * raggruppava tutto sotto "Non assegnato". L'unico modo per prevenire questa
 * categoria di bug è avere UN posto solo dove si elenca cosa estrarre.
 *
 * Regola: se aggiungi un campo al DB che ti serve in lettura, aggiungilo qui
 * UNA VOLTA. Tutti i chiamanti lo prenderanno automaticamente.
 *
 * ⚠️ PERCHE' ALCUNE QUERY DUPLICANO COMUNQUE L'ELENCO. Interpolando queste
 * costanti in `.select()` con un template literal, TypeScript perde l'inferenza
 * del risultato: il select diventa troppo complesso e si ottiene
 * "Type instantiation is excessively deep and possibly infinite", con TUTTI gli
 * accessi a valle che diventano errori (misurato: +55 su una sola query). I tipi
 * PostgREST si inferiscono solo da stringhe letterali.
 *
 * Il prezzo di quella duplicazione lo abbiamo gia' pagato: la query dei
 * preventivi teneva il proprio elenco a mano e vi mancavano `material_type`,
 * `board_typology` ed `en_520_type`. Riaprendo un preventivo salvato,
 * `wallModel.classifyMaterial` riceveva quei campi vuoti e disegnava OGNI lastra
 * come standard — niente pattern ignifugo ne' idrofugo. Nel configuratore si
 * vedeva giusto, nel preventivo no. Corretto il 2026-08-27.
 *
 * Quindi: quando aggiungi un campo qui, CONTROLLA anche gli elenchi inline di
 * `useEstimateStratigraphiesQuery.tsx` e degli hook di prezzo.
 */

/**
 * Sub-select materials usato dentro layers e finish_components_data embed.
 * Lista esaustiva dei campi tecnici + commerciali rilevanti per
 * configuratore, riepilogo materiali, ordine fornitore, calcoli costo.
 */
export const SELECT_MATERIAL_EMBED = `
  id,
  name,
  code,
  ean_code,
  color_hex,
  category,
  unit,
  unit_price,
  supplier,
  supplier_id,
  incidence_per_sqm,
  weight_per_sqm,
  thermal_conductivity,
  acoustic_performance,
  fire_resistance_class,
  compatible_board_types,
  width,
  length,
  box_pieces,
  installation_time_per_sqm,
  waste_percentage,
  disposal_percentage,
  thickness,
  material_type,
  board_typology,
  en_520_type,
  sheet_thickness,
  profile_type,
  surface_finish,
  fire_class
`;

/**
 * Shape full di una layer di stratigrafia (configuratore) con materials
 * principale + screw_material joined.
 */
export const SELECT_LAYER_FULL = `
  id,
  material_id,
  thickness,
  position,
  inter_axis,
  screw_material_id,
  screw_quantity,
  screw_cost_per_sqm,
  anchor_material_id,
  material_cost_per_sqm,
  installation_time_minutes,
  materials!layers_material_id_fkey ( ${SELECT_MATERIAL_EMBED} ),
  screw_materials:materials!layers_screw_material_id_fkey ( ${SELECT_MATERIAL_EMBED} ),
  anchor_materials:materials!layers_anchor_material_id_fkey ( ${SELECT_MATERIAL_EMBED} )
`;

/**
 * Shape di estimate_stratigraphy con stratigraphy + layers + materials
 * tutti embed (usato dal detail preventivo + export).
 */
export const SELECT_ESTIMATE_STRATIGRAPHY_FULL = `
  *,
  stratigraphies: stratigraphies!estimate_stratigraphies_stratigraphy_id_fkey (
    id,
    name,
    description,
    type,
    total_thickness,
    cost_per_sqm,
    is_certified,
    fire_resistance_class,
    acoustic_performance,
    weight_per_sqm,
    thermal_performance,
    layers: layers ( ${SELECT_LAYER_FULL} )
  )
`;

/**
 * Shape lightweight per la dashboard: include estimate_stratigraphies +
 * progetti embed esplicito (anti PGRST201 ambiguous FK) ma senza scendere
 * fino ai materiali — la dashboard aggrega su unit_cost e altri campi
 * già denormalizzati.
 */
export const SELECT_ESTIMATE_FOR_DASHBOARD = `
  *,
  projects!estimates_project_id_fkey ( id, name, customer_id ),
  estimate_stratigraphies (
    *,
    stratigraphy_data
  )
`;
